import ElementBaseConvert from '@src/slide/domain/ui-model/element-modeles/convert/elementBaseConvert';
import _has from 'lodash/has';
import _set from 'lodash/set';
import ShapeConvert from '@src/slide/domain/ui-model/element-modeles/convert/shapeConvert';
import type {
    ChartAxisUnits,
    ChartAxisUnitsLabel,
    ChartLegend,
    ChartPlotArea,
    ChartTitle,
    IAxisProps,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v2/chart-model.interface';

export default class ChartConvert extends ElementBaseConvert {
    public static async convertDataSdkToUi(sdkData: any, uiData: any, rawSdkData: any) {
        super.convertDataSdkToUi(sdkData, uiData, rawSdkData);

        if (_has(sdkData, 'graphicData.chart')) {
            this.convertChartSdkToUi(sdkData.graphicData.chart, uiData);
            ShapeConvert.convertDataSdkToUi(sdkData.graphicData.chart, uiData, undefined);
        }
    }

    public static convertDataUiToSdk(uiData, sdkData, rawUiData) {
        super.convertDataUiToSdk(uiData, sdkData, rawUiData);

        const chartSdkData = {};
        this.convertChartUiToSdk(uiData, chartSdkData);

        Object.keys(chartSdkData)
            .forEach((key) => {
                _set(sdkData, `graphicData.chart.${key}`, chartSdkData[key]);
            });
    }

    public static convertChartSdkToUi(sdkChart, uiData) {
        if (!sdkChart) return;// TODO 上报错误数据

        // 是一个数字，ooxml里为类似：<c14:style val="102"/>
        if (_has(sdkChart, 'style')) {
            uiData.style = sdkChart.style;
        }

        if (_has(sdkChart, 'fallbackImageSrc')) {
            uiData.fallbackImageSrc = sdkChart.fallbackImageSrc;
        }

        this.convertTitleSdkToUi(sdkChart, uiData);
        this.convertPlotAreaSdkToUi(sdkChart, uiData);
        this.convertLegendSdkToUi(sdkChart, uiData);
    }

    public static convertChartUiToSdk(uiData, sdkData) {
        // 是一个数字，ooxml里为类似：<c14:style val="102"/>
        if (_has(uiData, 'style')) {
            sdkData.style = uiData.style;
        }

        if (_has(uiData, 'fallbackImageSrc')) {
            sdkData.fallbackImageSrc = uiData.fallbackImageSrc;
        }

        this.convertTitleUiToSdk(uiData, sdkData);
        this.convertPlotAreaUiToSdk(uiData, sdkData);
        this.convertLegendUiToSdk(uiData, sdkData);
    }

    public static convertTitleSdkToUi(sdkChart, uiData) {
        const { title: sdkTitle } = sdkChart;
        if (!sdkTitle) {
            uiData.title = undefined; // 最外层标签，不能delete，否则会导致merge失败
            return;
        }

        const uiTitle: Partial<ChartTitle> = {};

        // 自定义标题文本属性、图形样式
        if (_has(sdkTitle, 'chartText')) {
            // @ts-ignore
            uiTitle.text = {};
            ShapeConvert.convertDataSdkToUi(sdkTitle.chartText, uiTitle.text, undefined);
        }

        // 标题默认文本属性、图形样式
        ShapeConvert.convertDataSdkToUi(sdkTitle, uiTitle, undefined);
        // 是否覆盖在图表上，转成bool
        uiTitle.overlay = !!sdkTitle.overlay;
        // 标题布局
        convertLayoutSdkToUi(sdkTitle, uiTitle);

        _set(uiData, 'title', uiTitle);
    }

    public static convertTitleUiToSdk(uiData, sdkData) {
        if (!_has(uiData, 'title')) {
            return;
        }
        const { title: uiTitle } = uiData;
        let sdkTitle: any = uiTitle;
        if (!sdkTitle) {
            _set(sdkData, 'title', sdkTitle);
            return;
        }
        sdkTitle = {};
        if (_has(uiTitle, 'text')) {
            sdkTitle.chartText = {};
            ShapeConvert.convertDataUiToSdk(uiTitle.text, sdkTitle.chartText, {});
        }

        ShapeConvert.convertDataUiToSdk(uiTitle, sdkTitle, {});
        if (_has(uiTitle, 'overlay')) {
            sdkTitle.overlay = convertBoolToNumber(uiTitle.overlay);
        }
        convertLayoutUiToSdk(uiTitle, sdkTitle);

        _set(sdkData, 'title', sdkTitle);
    }

    public static convertUnitsSdkToUi(sdkChart, uiData) {
        const { dispUnitsLbl, unit } = sdkChart?.dispUnits || {};
        const uiUnits: Partial<ChartAxisUnits> = {};
        if (unit) {
            uiUnits.unit = unit;
        }
        if (dispUnitsLbl) {
            const uiUnitLabel: Partial<ChartAxisUnitsLabel> = {};

            // 自定义标题文本属性、图形样式
            if (_has(dispUnitsLbl, 'chartText')) {
                // @ts-ignore
                uiUnitLabel.text = {};
                ShapeConvert.convertDataSdkToUi(dispUnitsLbl.chartText, uiUnitLabel.text, undefined);
            }

            // 默认文本属性、图形样式
            ShapeConvert.convertDataSdkToUi(dispUnitsLbl, uiUnitLabel, undefined);
            // 布局
            convertLayoutSdkToUi(dispUnitsLbl, uiUnitLabel);
            uiUnits.label = uiUnitLabel as ChartAxisUnitsLabel;
        }

        _set(uiData, 'units', uiUnits);
    }

    public static convertUnitsUiToSdk(uiData, sdkData) {
        if (_has(uiData, 'units.unit')) {
            _set(sdkData, 'dispUnits.unit', uiData.units.unit);
        }

        if (_has(uiData, 'units.label')) {
            const sdkDispUnitsLbl: any = {};
            const uiUnitLabel = uiData.units.label;

            if (_has(uiUnitLabel, 'text')) {
                sdkDispUnitsLbl.chartText = {};
                ShapeConvert.convertDataUiToSdk(uiUnitLabel.text, sdkDispUnitsLbl.chartText, {});
            }

            ShapeConvert.convertDataUiToSdk(uiUnitLabel, sdkDispUnitsLbl, {});
            convertLayoutUiToSdk(uiUnitLabel, sdkDispUnitsLbl);

            _set(sdkData, 'dispUnits.dispUnitsLbl', sdkDispUnitsLbl);
        }
    }

    public static convertPlotAreaSdkToUi(sdkChart, uiData) {
        if (!sdkChart.plotArea) return;
        const sdkPlotArea = sdkChart.plotArea;
        if (!sdkPlotArea) return;

        const {
            categoryAxes: sdkCategoryAxes,
            valueAxes: sdkValueAxes,
            dateAxes: sdkDateAxes,
        } = sdkPlotArea;
        const uiPlotArea: Partial<ChartPlotArea> = {};

        if (_has(sdkPlotArea, 'categoryAxes')) {
            this.convertAxesSdkToUi(sdkCategoryAxes, uiPlotArea, 'categoryAxes');
        }
        if (_has(sdkPlotArea, 'valueAxes')) {
            this.convertAxesSdkToUi(sdkValueAxes, uiPlotArea, 'valueAxes');
        }
        if (_has(sdkPlotArea, 'dateAxes')) {
            this.convertAxesSdkToUi(sdkDateAxes, uiPlotArea, 'dateAxes');
        }

        convertLayoutSdkToUi(sdkPlotArea, uiPlotArea);
        ShapeConvert.convertDataSdkToUi(sdkPlotArea, uiPlotArea, undefined);

        // 这么做是为了不覆盖plotArea.charts,这部分是另一个convert中转换的，走的convertArrayDataSdkToUi
        Object.keys(uiPlotArea)
            .forEach((key) => {
                _set(uiData, `plotArea.${key}`, uiPlotArea[key]);
            });
    }

    public static convertPlotAreaUiToSdk(uiData, sdkData) {
        if (!uiData.plotArea) return;

        const {
            categoryAxes: uiCategoryAxes,
            valueAxes: uiValueAxes,
            dateAxes: uiDateAxes,
        } = uiData.plotArea;

        const sdkPlotArea: any = {};
        this.convertAxesUiToSdk(uiCategoryAxes, sdkPlotArea, 'categoryAxes');
        this.convertAxesUiToSdk(uiValueAxes, sdkPlotArea, 'valueAxes');
        this.convertAxesUiToSdk(uiDateAxes, sdkPlotArea, 'dateAxes');

        convertLayoutUiToSdk(uiData.plotArea, sdkPlotArea);
        ShapeConvert.convertDataSdkToUi(uiData.plotArea, sdkPlotArea, {});

        _set(sdkData, 'plotArea', sdkPlotArea);
    }

    public static convertAxesSdkToUi(sdkAxes, uiPlotArea, axesType: string) {
        if (sdkAxes) {
            const uiAxes = sdkAxes.map(sdkAxis => this.convertAxisSdkToUi(sdkAxis));
            _set(uiPlotArea, axesType, uiAxes);
        }
    }

    public static convertAxesUiToSdk(uiAxes, sdkPlotArea, axesType: string) {
        if (uiAxes) {
            const sdkAxes = uiAxes.map(uiAxis => this.convertAxisUiToSdk(uiAxis));
            _set(sdkPlotArea, axesType, sdkAxes);
        } else {
            _set(sdkPlotArea, axesType, uiAxes);
        }
    }

    public static convertAxisSdkToUi(sdkAxis) {
        if (!sdkAxis) return;
        const uiAxis: Partial<IAxisProps> = {};

        // 坐标轴标题
        this.convertTitleSdkToUi(sdkAxis, uiAxis);
        // 显示单位
        this.convertUnitsSdkToUi(sdkAxis, uiAxis);
        // 图形、文本属性
        ShapeConvert.convertDataSdkToUi(sdkAxis, uiAxis, undefined);
        // 坐标轴位置
        uiAxis.position = sdkAxis.position;
        // 坐标轴缩放信息
        uiAxis.scaling = sdkAxis.scaling;
        uiAxis.numFmt = sdkAxis.numFmt;
        uiAxis.majorUnit = sdkAxis.majorUnit;
        uiAxis.minorUnit = sdkAxis.minorUnit;
        uiAxis.majorTickMark = sdkAxis.majorTickMark;
        uiAxis.minorTickMark = sdkAxis.minorTickMark;
        uiAxis.tickLabelPosition = sdkAxis.tickLabelPosition;
        uiAxis.crossBetween = sdkAxis.crossBetween;
        uiAxis.crosses = sdkAxis.crosses;
        uiAxis.crossAx = sdkAxis.crossAx;
        uiAxis.lblOffset = sdkAxis.lblOffset;
        uiAxis.axId = sdkAxis.axId;
        uiAxis.isDelete = !!sdkAxis.delete;
        uiAxis.autoLabeled = sdkAxis.autoLabeled;
        uiAxis.lblAlgn = sdkAxis.lblAlgn;

        if (sdkAxis.majorGridlines) {
            uiAxis.majorGridlines = {};
            // 辅助网格图形属性
            ShapeConvert.convertDataSdkToUi(sdkAxis.majorGridlines, uiAxis.majorGridlines, undefined);
        }
        if (sdkAxis.minorGridlines) {
            uiAxis.minorGridlines = {};
            // 辅助网格图形属性
            ShapeConvert.convertDataSdkToUi(sdkAxis.minorGridlines, uiAxis.minorGridlines, undefined);
        }
        return uiAxis;
    }

    // eslint-disable-next-line complexity
    public static convertAxisUiToSdk(uiAxis) {
        if (!uiAxis) return uiAxis;

        const sdkAxis: any = {};
        this.convertTitleUiToSdk(uiAxis, sdkAxis);
        this.convertUnitsUiToSdk(uiAxis, sdkAxis);
        ShapeConvert.convertDataUiToSdk(uiAxis, sdkAxis, {});

        // 坐标轴位
        setUiValueToSdkIfNotNil(sdkAxis, 'position', uiAxis.position);

        // 坐标轴缩放信
        setUiValueToSdkIfNotNil(sdkAxis, 'scaling', uiAxis.scaling);
        setUiValueToSdkIfNotNil(sdkAxis, 'numFmt', uiAxis.numFmt);
        setUiValueToSdkIfNotNil(sdkAxis, 'majorUnit', uiAxis.majorUnit);
        setUiValueToSdkIfNotNil(sdkAxis, 'minorUnit', uiAxis.minorUnit);
        setUiValueToSdkIfNotNil(sdkAxis, 'majorTickMark', uiAxis.majorTickMark);
        setUiValueToSdkIfNotNil(sdkAxis, 'minorTickMark', uiAxis.minorTickMark);
        setUiValueToSdkIfNotNil(sdkAxis, 'tickLabelPosition', uiAxis.tickLabelPosition);
        setUiValueToSdkIfNotNil(sdkAxis, 'crossBetween', uiAxis.crossBetween);
        setUiValueToSdkIfNotNil(sdkAxis, 'crosses', uiAxis.crosses);
        setUiValueToSdkIfNotNil(sdkAxis, 'crossAx', uiAxis.crossAx);
        setUiValueToSdkIfNotNil(sdkAxis, 'lblOffset', uiAxis.lblOffset);
        setUiValueToSdkIfNotNil(sdkAxis, 'axId', uiAxis.axId);
        setUiValueToSdkIfNotNil(sdkAxis, 'lblAlgn', uiAxis.lblAlgn);
        if (_has(uiAxis, 'isDelete')) {
            setUiValueToSdkIfNotNil(sdkAxis, 'delete', convertBoolToNumber(uiAxis.isDelete));
        }
        setUiValueToSdkIfNotNil(sdkAxis, 'autoLabeled', uiAxis.autoLabeled);

        if (uiAxis.majorGridlines !== undefined) {
            sdkAxis.majorGridlines = {};
            ShapeConvert.convertDataUiToSdk(uiAxis.majorGridlines, sdkAxis.majorGridlines, {});
        }
        if (uiAxis.minorGridlines !== undefined) {
            sdkAxis.minorGridlines = {};
            ShapeConvert.convertDataUiToSdk(uiAxis.minorGridlines, sdkAxis.minorGridlines, {});
        }

        return sdkAxis;
    }

    public static convertLegendSdkToUi(sdkChart, uiData) {
        const sdkLegend = sdkChart.legend;
        if (!sdkLegend) {
            uiData.legend = undefined;  // 最外层标签，不能delete，否则会导致merge失败
            return;
        }

        const uiLegend: Partial<ChartLegend> = {};

        uiLegend.position = sdkLegend.legendPosition;
        uiLegend.overlay = !!sdkLegend.overlay;
        // 图例布局
        convertLayoutSdkToUi(sdkLegend, uiLegend);

        ShapeConvert.convertDataSdkToUi(sdkLegend, uiLegend, undefined);

        if (sdkLegend.legendEntry) {
            uiLegend.legendEntryList = sdkLegend.legendEntry.map((legendEntryItem) => {
                const legendEntry = {
                    id: legendEntryItem.idx,
                    delete: !!legendEntryItem.delete,
                };
                // TODO 其实只用到了textBody，看看怎么shape/textBody处理分开好
                ShapeConvert.convertDataSdkToUi(legendEntryItem, legendEntry, undefined);

                return legendEntry;
            });
        }

        _set(uiData, 'legend', uiLegend);
    }


    public static convertLegendUiToSdk(uiData, sdkData) {
        if (!_has(uiData, 'legend')) {
            return;
        }
        const { legend: uiLegend } = uiData;
        if (!uiLegend) {
            _set(sdkData, 'legend', uiLegend);
            return;
        }
        const sdkLegend: any = {};

        const { position, overlay } = uiLegend;

        if (_has(uiLegend, 'position')) {
            setUiValueToSdkIfNotNil(sdkLegend, 'legendPosition', position);
        }
        if (_has(uiLegend, 'overlay')) {
            setUiValueToSdkIfNotNil(sdkLegend, 'overlay', convertBoolToNumber(overlay));
        }

        convertLayoutUiToSdk(uiLegend, sdkLegend);

        ShapeConvert.convertDataUiToSdk(uiLegend, sdkLegend, {});
        if (uiLegend.legendEntryList) {
            sdkLegend.legendEntry = uiLegend.legendEntryList.map((uiLegendEntryItem) => {
                const sdkLegendEntry = {
                    idx: uiLegendEntryItem.id,
                    delete: convertBoolToNumber(uiLegendEntryItem.delete),
                };
                ShapeConvert.convertDataSdkToUi(uiLegendEntryItem, sdkLegendEntry, {});

                return sdkLegendEntry;
            });
        }

        _set(sdkData, 'legend', sdkLegend);
    }
}

export function convertBoolToNumber(bool: boolean) {
    return bool ? 1 : 0;
}

export function convertLayoutSdkToUi(sdkData, uiData) {
    if (!_has(sdkData, 'layout')) {
        delete uiData.layout;  // sdk无数据，清空字段
        return;
    }

    uiData.layout = {};
    if (_has(sdkData, 'layout.leftMode')) {
        uiData.layout.leftMode = sdkData.layout.leftMode;
    }
    if (_has(sdkData, 'layout.topMode')) {
        uiData.layout.topMode = sdkData.layout.topMode;
    }
    if (_has(sdkData, 'layout.left')) {
        uiData.layout.left = Number(sdkData.layout.left);
    }
    if (_has(sdkData, 'layout.top')) {
        uiData.layout.top = Number(sdkData.layout.top);
    }
    if (_has(sdkData, 'layout.width')) {
        uiData.layout.width = Number(sdkData.layout.width);
    }
    if (_has(sdkData, 'layout.height')) {
        uiData.layout.height = Number(sdkData.layout.height);
    }
}

export function convertLayoutUiToSdk(uiData, sdkData) {
    if (!_has(uiData, 'layout')) {
        return;
    }
    if (!uiData.layout) { // 空值也需要赋值，因为可能是remove属性
        _set(sdkData, 'layout', uiData.layout);
        return;
    }

    sdkData.layout = {
        leftMode: uiData.layout.leftMode,
        topMode: uiData.layout.topMode,
    };

    if (_has(uiData, 'layout.left')) {
        sdkData.layout.left = uiData.layout.left;
    }
    if (_has(uiData, 'layout.top')) {
        sdkData.layout.top = uiData.layout.top;
    }
    if (_has(uiData, 'layout.width')) {
        sdkData.layout.width = uiData.layout.width;
    }
    if (_has(uiData, 'layout.height')) {
        sdkData.layout.height = uiData.layout.height;
    }
}

export function setUiValueToSdkIfNotNil(sdkData, key, value) {
    if (value !== undefined) {
        _set(sdkData, key, value);
    }
}
